import { useMemo, useCallback, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import {
  FileIcon,
  Search,
  Circle,
  CircleCheck,
  Download,
  ChevronUp,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { AuditSession, sessionSchema } from "../../models/audit/auditSession";
import { useDocumentData } from "../../hooks/useDocumentData";
import DocumentRow from "../../pages/audit/auditDocuments/DocumentRow";
import { fetchFastApi } from "../../api/fastapi";
import { toast } from "sonner";
import { useHookCache } from "../../lib/hookCache";
import { useProcedureSession } from "../../hooks/audit/useProcedureSession";
import { LoadingSpinner } from "../ui/loadingSpinner/loadingSpinner";
import { useAudit } from "../../contexts/auditContext";
import { controlsV2SessionApiBase } from "../../lib/sessionApiBase";
import { useDocumentRowAnalytics } from "../../hooks/audit/useDocumentRowAnalytics";
import { DocumentsHeaderRow } from "../../pages/audit/auditDocuments/DocumentsHeaderRow";
import { AnalyticsInputRefusalsNote } from "../analytics/AnalyticsInputRefusalsNote";
import { documentNamesById } from "../../lib/analyticsFeeds";
import { analyticsKeyPrefix } from "../../lib/analyticsOwner";
import { useSessionAnalyticsOwner } from "../../hooks/audit/useSessionAnalyticsOwner";

interface RematchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onConfirm: (instructions: string) => void;
  isSubmitting: boolean;
}

const RematchModal = ({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
  isSubmitting,
}: RematchModalProps) => {
  const [instructions, setInstructions] = useState("");

  const handleConfirm = () => {
    onConfirm(instructions);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Only clear on successful submit, not on cancel/close
    if (!newOpen && !isSubmitting) {
      setInstructions("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rematch Documents</DialogTitle>
          <DialogDescription>
            {selectedCount} document{selectedCount !== 1 ? "s" : ""} will be
            rematched. This will replace any existing matches for these
            documents.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Textarea
            placeholder="Optional: Add matching instructions..."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isSubmitting}>
            Rematch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface AuditDocumentsContentProps {
  procedureSessionId: string;
  controlSessionId: string;
  onDocumentSelect: (
    doc: AuditSession["documents"][0],
    auditSession: AuditSession,
  ) => void;
  currentIndex?: number;
  totalSessions?: number;
  onNavigate?: (index: number) => void;
}

type RowDoc = AuditSession["documents"][0];

export const AuditDocumentsContent = ({
  procedureSessionId,
  controlSessionId,
  onDocumentSelect,
  currentIndex,
  totalSessions,
  onNavigate,
}: AuditDocumentsContentProps) => {
  const { setEntry, invalidate } = useHookCache();
  const { sessionKind } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();
  const analyticsOwnerKind = analyticsOwner?.kind;
  const analyticsOwnerId = analyticsOwner?.id;
  const { data: auditSession } = useProcedureSession(procedureSessionId);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
    new Set(),
  );
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [isRematchModalOpen, setIsRematchModalOpen] = useState(false);
  const [isRematching, setIsRematching] = useState(false);

  const documentData = useDocumentData(auditSession);

  const showNavigation =
    currentIndex !== undefined &&
    totalSessions !== undefined &&
    totalSessions > 1 &&
    onNavigate;

  const handleDownloadAllDocuments = async () => {
    try {
      if (!controlSessionId) return;
      setDownloading(true);
      const blob = await fetchFastApi({
        method: "POST",
        path: `${controlsV2SessionApiBase(sessionKind, controlSessionId)}/download-documents`,
        body: {},
      });

      if (!(blob instanceof Blob)) {
        toast.error("Unexpected response from server");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documents-${controlSessionId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      console.error(e);
      toast.error("Failed to download documents");
    } finally {
      setDownloading(false);
    }
  };

  // Filter documents based on search term
  const filteredDocumentData = useMemo(() => {
    if (!searchTerm.trim()) {
      return documentData;
    }

    const searchLower = searchTerm.toLowerCase();
    const filterDocs = (docs: typeof documentData.matched) =>
      docs.filter((doc) => {
        const fileName = doc.fetchableDocument?.fileName || doc.name || "";
        return fileName.toLowerCase().includes(searchLower);
      });

    return {
      ...documentData,
      matched: filterDocs(documentData.matched),
      unmatched: filterDocs(documentData.unmatched),
    };
  }, [documentData, searchTerm]);

  // Get all visible document IDs for range selection
  const allVisibleDocIds = useMemo(() => {
    return [
      ...filteredDocumentData.unmatched,
      ...filteredDocumentData.matched,
    ].map((doc) => doc.documentId || doc.id);
  }, [filteredDocumentData]);

  // Handle checkbox toggle with shift-click range selection
  const handleDocumentCheckboxToggle = useCallback(
    (documentId: string, event: React.MouseEvent) => {
      if (event.shiftKey && lastSelectedId) {
        // Range select from lastSelectedId to current documentId
        const startIdx = allVisibleDocIds.indexOf(lastSelectedId);
        const endIdx = allVisibleDocIds.indexOf(documentId);

        if (startIdx !== -1 && endIdx !== -1) {
          const minIdx = Math.min(startIdx, endIdx);
          const maxIdx = Math.max(startIdx, endIdx);
          const rangeIds = allVisibleDocIds.slice(minIdx, maxIdx + 1);

          setSelectedDocumentIds((prev) => {
            const newSet = new Set(prev);
            rangeIds.forEach((id) => newSet.add(id));
            return newSet;
          });
        }
      } else {
        // Toggle single document
        setSelectedDocumentIds((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(documentId)) {
            newSet.delete(documentId);
          } else {
            newSet.add(documentId);
          }
          return newSet;
        });
        setLastSelectedId(documentId);
      }
    },
    [lastSelectedId, allVisibleDocIds],
  );

  // Clear selection when search term changes
  useEffect(() => {
    setSelectedDocumentIds(new Set());
    setLastSelectedId(null);
  }, [searchTerm]);

  // Get category options from procedure required documents
  const categoryOptions = useMemo(
    () =>
      auditSession?.procedure?.requiredDocuments?.map((doc) => ({
        value: doc.id,
        label: doc.shortName,
      })) || [],
    [auditSession?.procedure?.requiredDocuments],
  );

  const handleSampleChange = useCallback(
    async (documentId: string, sampleIds: string[]) => {
      if (!auditSession) return;
      // Find the document name for the toast
      const document = auditSession.documents?.find(
        (doc) => (doc.documentId || doc.id) === documentId,
      );
      const documentName =
        document?.name || document?.fetchableDocument?.fileName || "Document";

      setEntry<AuditSession>(["procedure-session", auditSession.id], (prev) => {
        // Remove existing assignments for this document
        const updatedSourceRows = prev.sourceRows.map((sourceRow) => ({
          ...sourceRow,
          matchItems: sourceRow.matchItems.filter(
            (matchItem) => matchItem.sourceDocument.id !== documentId,
          ),
        }));

        // Add new assignments
        const finalSourceRows = updatedSourceRows.map((sourceRow) => {
          if (sampleIds.includes(sourceRow.id)) {
            const fullDocument = prev.documents.find(
              (doc) => (doc.documentId || doc.id) === documentId,
            );

            const sourceDocument = {
              id: documentId,
              fileName:
                fullDocument?.fetchableDocument?.fileName || documentName,
              fileType:
                fullDocument?.fetchableDocument?.fileType || ("pdf" as const),
              fileUrl: fullDocument?.fetchableDocument?.fileUrl || "",
              fileContentHash:
                fullDocument?.fetchableDocument?.fileContentHash || "",
              state: fullDocument?.fetchableDocument?.state || "ready",
            };

            return {
              ...sourceRow,
              matchItems: [
                ...sourceRow.matchItems,
                {
                  id: `temp-${documentId}-${sourceRow.id}`,
                  extractionId: null,
                  extractionElementId: null,
                  confidenceScore: 1.0,
                  sourceDocument,
                  assignments: [],
                  state: "approved" as const,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
              ],
            };
          }
          return sourceRow;
        });

        return {
          ...prev,
          sourceRows: finalSourceRows,
        };
      });

      try {
        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/document/${documentId}/update-assigned-samples`,
          body: {
            assigned_samples: sampleIds,
          },
        });

        if (response?.session) {
          const newAuditSession = sessionSchema.parse(response.session);
          setEntry<AuditSession>(
            ["procedure-session", newAuditSession.id],
            () => newAuditSession,
          );
          // The full-session replace above refreshes the sample-card badge
          // (reads from auditSession.sourceRows[].matchItems), but the
          // Support tab's per-sample doc list reads from useSourceRow
          // (slim per-row cache, ["procedure-session", X, "source-rows", Y])
          // which isn't covered. Invalidate the slim source-rows prefix so
          // the newly-assigned doc shows up on the Support tab.
          void invalidate([
            "procedure-session",
            newAuditSession.id,
            "source-rows",
          ]);
          toast.success(`Sample assignments updated for ${documentName}`);
        } else {
          toast.error("Failed to update sample assignments");
        }
      } catch (error) {
        console.error("Error updating sample assignments:", error);
        toast.error("Error updating sample assignments");
      }
    },
    [auditSession, setEntry, invalidate],
  );

  const handleCategoryChange = useCallback(
    async (documentId: string, categoryIds: string[]) => {
      if (!auditSession) return;
      // Save previous state for potential rollback
      const previousState = auditSession;

      // Optimistically update the affected procedure-session cache entry.
      setEntry<AuditSession>(["procedure-session", auditSession.id], (prev) => {
        return {
          ...prev,
          documents: prev.documents.map((doc) => {
            const docUniqueId = doc.documentId ?? doc.id;
            if (docUniqueId === documentId) {
              return {
                ...doc,
                fetchableDocument: doc.fetchableDocument
                  ? {
                      ...doc.fetchableDocument,
                      documentAssignments: categoryIds.map((categoryId) => {
                        const requiredDoc =
                          prev.procedure.requiredDocuments.find(
                            (rd) => rd.id === categoryId,
                          );
                        return {
                          id: categoryId,
                          shortName: requiredDoc?.shortName || categoryId,
                          description: requiredDoc?.description || null,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        };
                      }),
                    }
                  : null,
              };
            }
            return doc;
          }),
          // Also update matchItems in sourceRows
          sourceRows: prev.sourceRows.map((sourceRow) => ({
            ...sourceRow,
            matchItems: sourceRow.matchItems.map((matchItem) => {
              if (matchItem.sourceDocument.id === documentId) {
                return {
                  ...matchItem,
                  assignments: categoryIds.map((categoryId) => ({
                    id: crypto.randomUUID(),
                    procedureDocumentId: categoryId,
                    assignerId: null,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  })),
                };
              }
              return matchItem;
            }),
          })),
        };
      });

      try {
        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/document/${documentId}/update-document-assignments`,
          body: {
            document_assignments: categoryIds,
          },
        });
        toast.success("Document category updated");
        await invalidate(["procedure-session", auditSession.id]);
      } catch (error) {
        console.error("Error updating document category:", error);
        toast.error("Error updating document category");

        // Rollback on error: restore the previous procedure-session snapshot.
        setEntry<AuditSession>(
          ["procedure-session", auditSession.id],
          () => previousState,
        );
      }
    },
    [auditSession, setEntry, invalidate],
  );

  const handleRemoveDocument = useCallback(
    async (documentId: string) => {
      if (!auditSession) return;
      // find document.id in auditSession.documents
      const document = auditSession.documents.find(
        (d) => d.documentId === documentId || d.id === documentId,
      );

      if (!document) {
        toast.error("Document not found");
        return;
      }

      // Optimistically remove the doc from the procedure-session cache entry.
      setEntry<AuditSession>(["procedure-session", auditSession.id], (prev) => {
        return {
          ...prev,
          documents: (prev.documents || []).filter(
            (d) => (d.documentId || d.id) !== documentId,
          ),
          sourceRows: (prev.sourceRows || []).map((row) => ({
            ...row,
            matchItems: (row.matchItems || []).filter(
              (mi) => mi.sourceDocument.id !== documentId,
            ),
          })),
        };
      });

      try {
        await fetchFastApi({
          method: "DELETE",
          path: `/matching/match-session/${auditSession.id}/document/${document.id}`,
        });
        toast.success("Document removed");
        await invalidate(["procedure-session", auditSession.id]);
      } catch (error) {
        console.error("Failed to remove document", error);
        // Invalidate to refetch authoritative state on error
        void invalidate(["procedure-session", auditSession.id]);
        toast.error("Failed to remove document");
      }
      // Removing a document drops the analytics input bindings that name it
      // server-side, and bindings are cached per analytics owner, so the
      // procedure-session sweep above doesn't reach them. The backend can
      // answer 500 after committing the delete, so refresh them either way.
      if (analyticsOwnerKind && analyticsOwnerId) {
        void invalidate(
          analyticsKeyPrefix({
            kind: analyticsOwnerKind,
            id: analyticsOwnerId,
          }),
        );
      }
    },
    [auditSession, setEntry, invalidate, analyticsOwnerKind, analyticsOwnerId],
  );

  // The analytics columns render only for a procedure that has an analytics
  // column — exactly as `Sample Match` renders only under a sample basis. This
  // component is shared with sessions that have neither.
  const rowAnalytics = useDocumentRowAnalytics(
    procedureSessionId,
    auditSession?.procedure,
    analyticsOwner,
  );

  // Built here rather than in the analytics hook: a binding stores a document
  // id, and the session's documents are this page's own read. Memoised because
  // every row is compared on prop identity.
  const documentNames = useMemo(
    () => documentNamesById(auditSession?.documents ?? []),
    [auditSession?.documents],
  );

  const handleRematchConfirm = useCallback(
    async (instructions: string) => {
      if (!auditSession) return;
      try {
        setIsRematching(true);
        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/rematch-documents`,
          body: {
            document_ids: Array.from(selectedDocumentIds),
            matching_instructions: instructions || null,
          },
        });
        toast.success(
          `Rematch started for ${selectedDocumentIds.size} document${selectedDocumentIds.size !== 1 ? "s" : ""}`,
        );
        setIsRematchModalOpen(false);
        setSelectedDocumentIds(new Set());
        // Refresh the control session caches (envelope + per-procedure slices)
        await invalidate(["control-session", controlSessionId]);
        await invalidate(["procedure-session", auditSession.id]);
      } catch (error) {
        console.error("Error starting rematch:", error);
        toast.error("Failed to start rematch");
      } finally {
        setIsRematching(false);
      }
    },
    [auditSession, selectedDocumentIds, invalidate, controlSessionId],
  );

  if (!auditSession) {
    return (
      <div className="flex flex-col flex-1 min-h-0 items-center justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between">
        {showNavigation && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {currentIndex + 1} of {totalSessions}
            </span>
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() => onNavigate(currentIndex - 1)}
                disabled={currentIndex === 0}
                title="Previous procedure"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() => onNavigate(currentIndex + 1)}
                disabled={currentIndex === totalSessions - 1}
                title="Next procedure"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-semibold">
          {auditSession.procedure.name} (
          {documentData.matched.length + documentData.unmatched.length || 0})
        </div>
        <div className="flex items-center gap-2">
          {selectedDocumentIds.size > 0 && (
            <Button
              variant="outline"
              onClick={() => setIsRematchModalOpen(true)}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Rematch ({selectedDocumentIds.size})
            </Button>
          )}
          <Button
            className="flex items-center gap-2"
            variant="outline"
            onClick={handleDownloadAllDocuments}
            loading={downloading}
          >
            <Download className="h-4 w-4" />
            Download Files
          </Button>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            placeholder="Search docs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Documents List */}
      <div className="border rounded-md flex-1 min-h-0 overflow-auto">
        <div className="min-w-[800px]">
          <DocumentsHeaderRow
            procedureBasis={auditSession.procedure.procedureBasis}
            hasAnalytics={rowAnalytics !== undefined}
          />

          {/* Unmatched Documents Section */}
          {filteredDocumentData.unmatched.length > 0 && (
            <>
              {/* Section Header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-white border-b sticky top-[36.5px] z-[9]">
                <Circle className="w-4 h-4" />
                <span className="text-red-600 font-medium text-sm">
                  {filteredDocumentData.unmatched.length} Unmatched
                </span>
              </div>

              {/* Unmatched Rows */}
              {filteredDocumentData.unmatched.map((doc) => {
                const documentId = doc.documentId || doc.id;
                const sampleValues =
                  filteredDocumentData.sampleValuesMap.get(documentId) || [];

                return (
                  <DocumentRow
                    key={doc.documentId}
                    doc={doc}
                    isSelected={false}
                    isChecked={selectedDocumentIds.has(documentId)}
                    onCheckboxToggle={handleDocumentCheckboxToggle}
                    sampleValues={sampleValues}
                    categoryOptions={categoryOptions}
                    onDocumentSelect={(d: RowDoc) =>
                      onDocumentSelect(d, auditSession)
                    }
                    onSampleChange={handleSampleChange}
                    onCategoryChange={handleCategoryChange}
                    sampleOptions={filteredDocumentData.sampleOptions.sort(
                      (a, b) => (a.sampleNumber || 0) - (b.sampleNumber || 0),
                    )}
                    auditSession={auditSession}
                    onRemoveDocument={handleRemoveDocument}
                    analytics={rowAnalytics}
                    documentNames={documentNames}
                  />
                );
              })}
            </>
          )}

          {/* Matched Documents Section */}
          {filteredDocumentData.matched.length > 0 && (
            <>
              {/* Section Header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-white border-b sticky top-[36.5px] z-[9]">
                <CircleCheck className="w-4 h-4 text-green-800" />
                <span className="text-green-800 text-sm">
                  {filteredDocumentData.matched.length} Matched
                </span>
              </div>

              {/* Matched Rows */}
              {filteredDocumentData.matched.map((doc) => {
                const documentId = doc.documentId || doc.id;
                const sampleValues =
                  filteredDocumentData.sampleValuesMap.get(documentId) || [];

                return (
                  <DocumentRow
                    key={doc.documentId}
                    doc={doc}
                    isSelected={false}
                    isChecked={selectedDocumentIds.has(documentId)}
                    onCheckboxToggle={handleDocumentCheckboxToggle}
                    sampleValues={sampleValues}
                    categoryOptions={categoryOptions}
                    onDocumentSelect={(d: RowDoc) =>
                      onDocumentSelect(d, auditSession)
                    }
                    onSampleChange={handleSampleChange}
                    onCategoryChange={handleCategoryChange}
                    sampleOptions={filteredDocumentData.sampleOptions.sort(
                      (a, b) => (a.sampleNumber || 0) - (b.sampleNumber || 0),
                    )}
                    auditSession={auditSession}
                    onRemoveDocument={handleRemoveDocument}
                    analytics={rowAnalytics}
                    documentNames={documentNames}
                  />
                );
              })}
            </>
          )}
        </div>

        {/* Empty State */}
        {auditSession?.documents?.length === 0 && (
          <div className="px-1 py-10 text-center text-stone-500">
            <FileIcon className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <div className="text-lg font-medium mb-2">No documents found</div>
            <div className="text-sm">Upload documents to get started</div>
          </div>
        )}
      </div>

      {rowAnalytics !== undefined && (
        <AnalyticsInputRefusalsNote
          targets={rowAnalytics.targets}
          // The whole table's documents and samples rather than the filtered
          // ones: the flag and the match are resolved for the test table, and a
          // search term is not allowed to change what a run will refuse.
          documents={auditSession.documents}
          sourceRows={auditSession.sourceRows}
          documentNames={documentNames}
          feedsState={rowAnalytics.feedsState}
        />
      )}

      <RematchModal
        open={isRematchModalOpen}
        onOpenChange={setIsRematchModalOpen}
        selectedCount={selectedDocumentIds.size}
        onConfirm={handleRematchConfirm}
        isSubmitting={isRematching}
      />
    </div>
  );
};
